import React, { forwardRef, type ComponentType, type SVGProps } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { LoaderIcon } from "@components/icons";

interface IIconButton {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  className?: string;
  iconClassName?: string;
  isLoading?: boolean;
  disableGroup?: boolean;
}

export interface IIconButtonProps
  extends IIconButton,
    React.ButtonHTMLAttributes<HTMLButtonElement> {}

export interface IIconButtonLinkProps
  extends IIconButton,
    React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Anchor link.
   */
  href?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IIconButtonProps>(
  (props, ref) => {
    const {
      className,
      disableGroup,
      iconClassName,
      icon: Icon,
      isLoading,
      ...rest
    } = props;

    const ButtonIcon = isLoading ? LoaderIcon : Icon;

    return (
      <button
        className={twMerge(!disableGroup && "group", className)}
        ref={ref}
        disabled={isLoading}
        {...rest}
      >
        <ButtonIcon
          className={twMerge(
            "text-grey-dark",
            disableGroup
              ? "hover:text-base-black active:text-base-black"
              : "group-hover:text-base-black group-active:text-base-black",
            isLoading && "animate-spin",
            iconClassName,
          )}
        />
      </button>
    );
  },
);

export const IconButtonLink = forwardRef<
  HTMLAnchorElement,
  IIconButtonLinkProps
>((props, ref) => {
  const {
    className,
    iconClassName,
    icon: Icon,
    isLoading,
    href,
    ...rest
  } = props;

  const ButtonIcon = isLoading ? LoaderIcon : Icon;

  return React.createElement(
    href ? Link : "a",
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    {
      className: twMerge("group", className),
      ...(href ? { to: href } : {}),
      ref,
      ...rest,
    },
    <ButtonIcon
      className={twMerge(
        "text-grey-dark group-hover:text-base-black group-active:text-base-black",
        isLoading && "animate-spin",
        iconClassName,
      )}
    />,
  );
});
